import { ApolloError, useQuery } from '@apollo/client';
import {
    AnyAsset,
    AnyDebt,
    AnyDepositoryAccount,
    AnyFinancialEntity,
    ExternalFinancialEntity,
    ExternalFinancialEntityType,
    FEGetter,
    FinancialEntityTypename,
    getCurrentValue,
    getFEBaseConfig,
    isAsset,
    isDebt,
    isDepositoryAccount,
    isLinkedAndConnected,
    PlentyGoalAccountFinancialEntity,
    RetirementAccountFinancialEntity,
    retirementAccountSubtypeLabelMap,
} from '../../financialEntities';
import {
    AllFinancialEntitiesDocument,
    FinancialEntityFieldsFragment,
    FinancialEntityOwnershipType,
    RetirementAccountType,
} from '../../graphql/generated';
import { sortBy } from '../../util/arrays';
import { dt, DT } from '../../util/dateTime';
import { typedEntries } from '../../util/objects';
import { isNotNullish } from '../../util/types';
import { IconName } from '../icons';

/**
 * Canonical definition of whether current user owns a given FE:
 * 1. User added the FE; and
 * 2. Partner is not a joint owner of the FE.
 */
export const isMyFE = (fe: AnyFinancialEntity): boolean => {
    // For depository accounts, `jointOwnerPartnershipId` determines
    // whether the partner is a joint owner.
    if (fe.type !== 'PlentyGoalAccount' && isDepositoryAccount(fe)) {
        return fe.isMine && !fe.jointOwnerPartnershipId;
    }

    // For all other account types, `ownershipType` and `shareStatus` determine
    // whether the partner is a joint owner. We assume that all joint & shared
    // accounts have the partner as a joint owner.
    return (
        fe.isMine &&
        !(fe.ownershipType === 'JOINT' && fe.shareStatus === 'SHARED')
    );
};

export const getInstitutionName = (
    fe: ExternalFinancialEntity
): string | null | undefined => {
    return fe.institution?.name;
};

export function assetsFromFEs(
    fes?: FinancialEntityFieldsFragment[] | null
): AnyAsset[];
export function assetsFromFEs(fes?: unknown): unknown {
    if (!Array.isArray(fes) || !fes.length) return [];
    return fes.filter((fe) => isAsset(fe) && !fe.deletedAt);
}

export const usePlentyAccountClosingDepositoryAccounts = (
    selectableDepositoryAccountIds: string[]
): {
    loading: boolean;
    error?: ApolloError;
    accounts: AnyDepositoryAccount[];
} => {
    const { data, loading, error } = useQuery(AllFinancialEntitiesDocument);
    if (!data || loading || error) return { loading, error, accounts: [] };
    const accounts = depositoryAccountsFromFEs(data.financialEntities).filter(
        (account) =>
            account.isMine &&
            selectableDepositoryAccountIds.includes(account.id)
    );
    return { loading, accounts };
};

export function depositoryAccountsFromFEs(
    fes?: FinancialEntityFieldsFragment[] | null
): AnyDepositoryAccount[];
export function depositoryAccountsFromFEs(fes?: unknown): unknown {
    if (!Array.isArray(fes) || !fes.length) return [];
    return fes.filter((fe) => isDepositoryAccount(fe) && !fe.deletedAt);
}

export function debtsFromFEs(
    fes?: FinancialEntityFieldsFragment[] | null
): AnyDebt[];
export function debtsFromFEs(fes?: unknown): unknown {
    if (!Array.isArray(fes) || !fes.length) return [];
    return fes.filter((fe) => isDebt(fe) && !fe.deletedAt);
}

export const minDateFromFEs = (
    financialEntities?: FinancialEntityFieldsFragment[] | null
): string => {
    let minIso = dt().toISOString();

    financialEntities?.forEach((fe) => {
        const feMinIso = minDateForFE(fe);
        if (feMinIso) {
            minIso = feMinIso < minIso ? feMinIso : minIso;
        }
    });

    return dt(minIso).toDateStr();
};

export const minDateForFE = (fe: FinancialEntityFieldsFragment): string => {
    const feMinIso = fe.earliestSnapshot?.dateTime;
    return feMinIso ? dt(feMinIso).toDateStr() : dt().toDateStr();
};

export const getLastUpdatedBalanceDate = (fe: {
    updatedAt: string;
    createdAt: string;
    latestSnapshot?: { dateTime: string } | null;
}): DT => {
    if ('latestSnapshot' in fe && fe.latestSnapshot) {
        return dt(fe.latestSnapshot.dateTime);
    }
    return dt(fe.updatedAt ?? fe.createdAt);
};

export const getTransactionsLastUpdatedAt = (fe: {
    transactionsUpdatedAt?: string | null | undefined;
}): DT | undefined => {
    if (fe.transactionsUpdatedAt) {
        return dt(fe.transactionsUpdatedAt);
    }

    return undefined;
};

export const isLatestBalanceAccurate = (fe: AnyFinancialEntity): boolean => {
    if ('latestSnapshot' in fe && fe.latestSnapshot) {
        return fe.latestSnapshot.isBalanceDateAccurate;
    }

    return true;
};

export const isLinkingInProgress = (fe: ExternalFinancialEntity): boolean => {
    return typeof getCurrentValue(fe) !== 'number';
};

export const canLink = (fe: ExternalFinancialEntity): boolean => {
    return (
        getFEBaseConfig(fe.type).isLinkable &&
        !isLinkedAndConnected(fe) &&
        fe.isMine
    );
};

export const isDisconnectedOrError = (fe: ExternalFinancialEntity): boolean => {
    return (
        fe.linkStatus === 'PLAID_DISCONNECTED' ||
        fe.linkStatus === 'PLAID_ERROR' ||
        fe.linkStatus === 'MONEY_KIT_DISCONNECTED' ||
        fe.linkStatus === 'MONEY_KIT_ERROR'
    );
};

export const getIconName: FEGetter<IconName> = (feType) => {
    const config = getFEBaseConfig(feType);
    if ('iconName' in config) return config.iconName;
    return 'CurrencyDollarCircle';
};

export const getColor: FEGetter<string> = (feType) => {
    return getFEBaseConfig(feType).color;
};

export type FinancialEntityCombinedType =
    | 'BrokerageAccount'
    | 'CarLoan'
    | 'Car'
    | 'CashHolding'
    | 'CD'
    | 'CheckingAccount'
    | 'CreditCard'
    | 'HomeMortgage'
    | 'HSA'
    | 'LineOfCredit'
    | 'MoneyMarketAccount'
    | 'OtherAsset'
    | 'OtherInvestment'
    | 'OtherLiability'
    | 'RealEstateProperty'
    | 'RetirementAccount_IRA'
    | 'RetirementAccount_KEOGH'
    | 'RetirementAccount_PENSION'
    | 'RetirementAccount_ROTH_401K'
    | 'RetirementAccount_ROTH_IRA'
    | 'RetirementAccount_SEP_IRA'
    | 'RetirementAccount_THRIFT_SAVINGS_PLAN'
    | 'RetirementAccount__401A'
    | 'RetirementAccount__401K'
    | 'RetirementAccount__403B'
    | 'RetirementAccount__457B'
    | 'S529Account'
    | 'SavingsAccount'
    | 'StockPlan'
    | 'StudentLoan';

export const configByFECombinedType: Record<
    FinancialEntityCombinedType,
    {
        label: string;
        type: FinancialEntityTypename;
        subtype: RetirementAccountType | null;
    }
> = {
    BrokerageAccount: {
        label: getFEBaseConfig('BrokerageAccount').title,
        type: 'BrokerageAccount',
        subtype: null,
    },
    Car: {
        label: getFEBaseConfig('Car').title,
        type: 'Car',
        subtype: null,
    },
    CarLoan: {
        label: getFEBaseConfig('CarLoan').title,
        type: 'CarLoan',
        subtype: null,
    },
    CashHolding: {
        label: getFEBaseConfig('CashHolding').title,
        type: 'CashHolding',
        subtype: null,
    },
    CD: {
        label: getFEBaseConfig('CD').title,
        type: 'CD',
        subtype: null,
    },
    CheckingAccount: {
        label: getFEBaseConfig('CheckingAccount').title,
        type: 'CheckingAccount',
        subtype: null,
    },
    CreditCard: {
        label: getFEBaseConfig('CreditCard').title,
        type: 'CreditCard',
        subtype: null,
    },
    HomeMortgage: {
        label: getFEBaseConfig('HomeMortgage').title,
        type: 'HomeMortgage',
        subtype: null,
    },
    HSA: {
        label: getFEBaseConfig('HSA').title,
        type: 'HSA',
        subtype: null,
    },
    LineOfCredit: {
        label: getFEBaseConfig('LineOfCredit').title,
        type: 'LineOfCredit',
        subtype: null,
    },
    MoneyMarketAccount: {
        label: getFEBaseConfig('MoneyMarketAccount').title,
        type: 'MoneyMarketAccount',
        subtype: null,
    },
    OtherAsset: {
        label: getFEBaseConfig('OtherAsset').title,
        type: 'OtherAsset',
        subtype: null,
    },
    OtherInvestment: {
        label: getFEBaseConfig('OtherInvestment').title,
        type: 'OtherInvestment',
        subtype: null,
    },
    OtherLiability: {
        label: getFEBaseConfig('OtherLiability').title,
        type: 'OtherLiability',
        subtype: null,
    },
    RealEstateProperty: {
        label: getFEBaseConfig('RealEstateProperty').title,
        type: 'RealEstateProperty',
        subtype: null,
    },
    RetirementAccount_IRA: {
        label: retirementAccountSubtypeLabelMap['IRA'],
        type: 'RetirementAccount',
        subtype: 'IRA',
    },
    RetirementAccount_ROTH_401K: {
        label: retirementAccountSubtypeLabelMap['ROTH_401K'],
        type: 'RetirementAccount',
        subtype: 'ROTH_401K',
    },
    RetirementAccount_ROTH_IRA: {
        label: retirementAccountSubtypeLabelMap['ROTH_IRA'],
        type: 'RetirementAccount',
        subtype: 'ROTH_IRA',
    },
    RetirementAccount_SEP_IRA: {
        label: retirementAccountSubtypeLabelMap['SEP_IRA'],
        type: 'RetirementAccount',
        subtype: 'SEP_IRA',
    },
    RetirementAccount__401A: {
        label: retirementAccountSubtypeLabelMap['_401A'],
        type: 'RetirementAccount',
        subtype: '_401A',
    },
    RetirementAccount__401K: {
        label: retirementAccountSubtypeLabelMap['_401K'],
        type: 'RetirementAccount',
        subtype: '_401K',
    },
    RetirementAccount__403B: {
        label: retirementAccountSubtypeLabelMap['_403B'],
        type: 'RetirementAccount',
        subtype: '_403B',
    },
    RetirementAccount__457B: {
        label: retirementAccountSubtypeLabelMap['_457B'],
        type: 'RetirementAccount',
        subtype: '_457B',
    },
    RetirementAccount_KEOGH: {
        label: retirementAccountSubtypeLabelMap['KEOGH'],
        type: 'RetirementAccount',
        subtype: 'KEOGH',
    },
    RetirementAccount_PENSION: {
        label: retirementAccountSubtypeLabelMap['PENSION'],
        type: 'RetirementAccount',
        subtype: 'PENSION',
    },
    RetirementAccount_THRIFT_SAVINGS_PLAN: {
        label: retirementAccountSubtypeLabelMap['THRIFT_SAVINGS_PLAN'],
        type: 'RetirementAccount',
        subtype: 'THRIFT_SAVINGS_PLAN',
    },
    S529Account: {
        label: getFEBaseConfig('S529Account').title,
        type: 'S529Account',
        subtype: null,
    },
    SavingsAccount: {
        label: getFEBaseConfig('SavingsAccount').title,
        type: 'SavingsAccount',
        subtype: null,
    },
    StockPlan: {
        label: getFEBaseConfig('StockPlan').title,
        type: 'StockPlan',
        subtype: null,
    },
    StudentLoan: {
        label: getFEBaseConfig('StudentLoan').title,
        type: 'StudentLoan',
        subtype: null,
    },
};

export const getFECombinedType = (
    info: ExternalFinancialEntity['info']
): FinancialEntityCombinedType => {
    return info.feType === 'RetirementAccount'
        ? `RetirementAccount_${info.subtype}`
        : info.feType;
};

export const getFECombinedTypeOptions = (
    feType: ExternalFinancialEntityType,
    ownershipType: FinancialEntityOwnershipType
) => {
    return sortBy(
        typedEntries(configByFECombinedType)
            .map(([feCombinedType, config]) => {
                const { canBeJoint } = getFEBaseConfig(feType);
                if (ownershipType === 'JOINT' && !canBeJoint) return null;
                return {
                    value: feCombinedType,
                    label: config.label.replace(' Account', ''),
                };
            })
            .filter(isNotNullish),
        'label'
    );
};

// TODO: Move this into a helper function (and consider taking age into account)
const IRA_CONTRIBUTION_LIMIT_STR =
    'The 2024 contribution limit is $7,000 for those under 50. It’s $8,000 for those 50 and above. Contribute by April 15, 2025.';
const _401K_403B_CONTRIBUTION_LIMIT_STR =
    'The 2024 contribution limit is $23,000. Contribute by December 31st, 2024.';

export const getRetirementContributionLimitContent = (
    fe: RetirementAccountFinancialEntity,
    {
        GENERIC_RETIREMENT_CONTRIBUTION_LIMIT_CONTENT,
    }: { GENERIC_RETIREMENT_CONTRIBUTION_LIMIT_CONTENT: React.ReactNode }
): React.ReactNode => {
    const { subtype } = fe.info;
    switch (subtype) {
        case 'IRA':
        case 'ROTH_IRA':
            return IRA_CONTRIBUTION_LIMIT_STR;
        case '_401K':
        case 'ROTH_401K':
        case '_403B':
            return _401K_403B_CONTRIBUTION_LIMIT_STR;
        case 'SEP_IRA':
        case '_401A':
        case '_457B':
        case 'PENSION':
        case 'KEOGH':
        case 'THRIFT_SAVINGS_PLAN':
            return GENERIC_RETIREMENT_CONTRIBUTION_LIMIT_CONTENT;
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = subtype;
        }
    }
};

export const getGoalAccountContributionLimitStr = (
    fe: PlentyGoalAccountFinancialEntity
): string | undefined => {
    const { accountType } = fe.info;
    switch (accountType) {
        case 'TRADITIONAL_IRA':
        case 'ROTH_IRA':
            return IRA_CONTRIBUTION_LIMIT_STR;
        case 'BROKERAGE':
        case 'JOINT_BROKERAGE':
            return; // no contribution limit
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = accountType;
        }
    }
};
